import {
  NoSmartWalletAccountSignerFoundError,
  getOwnerWalletAccountForSmartWalletAccount,
} from '@dynamic-labs-sdk/client';
import { getDefaultClient } from '@dynamic-labs-sdk/client/core';
import {
  type EvmWalletAccount,
  isEvmWalletAccount,
} from '@dynamic-labs-sdk/evm';
import { createWalletClientForWalletAccount } from '@dynamic-labs-sdk/evm/viem';
import type { Account, Chain, Transport, WalletClient } from 'viem';

type GetSignerForSmartWalletAccountParams = {
  smartWalletAccount: EvmWalletAccount;
};

/**
 * Gets a WalletClient instance for the EOA of the smart wallet account.
 * You can use this signer to sign EVM transactions.
 *
 * @param params.smartWalletAccount - The EVM smart wallet account to get the signer for.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to an WalletClient instance.
 * @throws NoSmartWalletAccountSignerFoundError When the signer wallet account is not found for the given smart wallet account.
 */
export const getSignerForSmartWalletAccount = (
  { smartWalletAccount }: GetSignerForSmartWalletAccountParams,
  client = getDefaultClient()
): Promise<WalletClient<Transport, Chain, Account>> => {
  const signerWalletAccount = getOwnerWalletAccountForSmartWalletAccount(
    { smartWalletAccount },
    client
  );

  if (!signerWalletAccount || !isEvmWalletAccount(signerWalletAccount)) {
    throw new NoSmartWalletAccountSignerFoundError(smartWalletAccount.address);
  }

  return createWalletClientForWalletAccount(
    {
      walletAccount: signerWalletAccount,
    },
    client
  );
};
